// Component: Tooltip
// Purpose: Reusable tooltip component with CSS transitions, debouncing, and portal support

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export function Tooltip({ text, children }) {
  const [isVisible, setIsVisible] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  
  // Show tooltip with delay to prevent flickering on accidental hover
  const showTooltip = () => {
    // Clear any existing timeout to prevent flickering
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set visibility first, then fade in
    setIsVisible(true);
    // Small delay before showing to prevent flash on quick mouse movements
    timeoutRef.current = setTimeout(() => {
      setOpacity(1);
    }, 50);
  };
  
  // Hide tooltip with transition
  const hideTooltip = () => {
    // Start fade out animation
    setOpacity(0);
    // Remove from DOM after transition completes
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 200); // Match transition duration
  };
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // Calculate and set position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        // On mobile, position tooltip centered and below
        setPosition({
          top: rect.bottom + 10,
          left: window.innerWidth / 2 - 140, // Approximate center
        });
      } else {
        // On desktop, position below the trigger
        setPosition({
          top: rect.bottom + 10,
          left: rect.left,
        });
      }
    }
  }, [isVisible]);
  
  // Portal element with transitions
  const tooltipElement = (
    <div 
      ref={tooltipRef}
      className="fixed z-[9999] bg-gray-800 text-white rounded-md shadow-lg transition-opacity duration-200"
      style={{ 
        top: `${position.top}px`, 
        left: `${position.left}px`,
        maxWidth: '280px',
        padding: '12px 16px',
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        opacity: opacity,
        pointerEvents: 'none', // Prevents the tooltip from interfering with mouse events
      }}
    >
      <div 
        className="absolute bottom-full left-4" 
        style={{ 
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderBottom: '8px solid #1f2937',
        }}
      ></div>
      {text}
    </div>
  );

  return (
    <>
      <div 
        ref={triggerRef}
        className="inline-block"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>
      {isVisible && createPortal(tooltipElement, document.body)}
    </>
  );
}

export default Tooltip;
